/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Conversational Chat Studio View (Task 10.3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  useChatStore,
  type ChatMessageItem,
} from '../stores/useChatStore';
import {
  SYSTEM_CHAT_PERSONAS,
  CHAT_AI_MODELS,
  type EnrichedChatPersona,
} from '../data/chat-personas.catalog';
import { ChatSidebar } from '../components/chat/ChatSidebar';
import {
  PersonaSelectorModal,
  ModelSelectorDropdown,
  getPersonaIcon,
} from '../components/chat/PersonaSelector';
import { MultimodalChatInput, type MultimodalMessagePayload } from '../components/chat/MultimodalChatInput';
import { ChatMessageFeed } from '../components/chat/ChatMessageFeed';
import { ChatStreamService } from '../services/chat-stream.service';
import { CitationsDrawer } from '../components/chat/CitationsDrawer';
import { ShareChatModal } from '../components/chat/ShareChatModal';
import { useI18n } from '../hooks/useI18n';
import {
  PanelLeftClose,
  PanelLeft,
  Sparkles,
  Database,
  Pin,
  PinOff,
  Plus,
  ArrowRight,
  Bot,
  Share2,
} from 'lucide-react';

interface ChatStudioViewProps {
  onNavigateToTemplates?: () => void;
  onNavigateToRAG?: () => void;
}

export const ChatStudioView: React.FC<ChatStudioViewProps> = ({
  onNavigateToTemplates,
  onNavigateToRAG,
}) => {
  const { t } = useI18n();
  const {
    activePersonaId,
    activeModelCode,
    isSidebarOpen,
    isGenerating,
    toggleSidebar,
    setActivePersona,
    setActiveModel,
    getActiveSession,
    togglePinSession,
    createSession,
    updateSessionTitle,
    incrementSessionMessages,
    addMessage,
    appendMessageDelta,
    finalizeMessage,
    updateMessageContent,
    setIsGenerating,
    getSessionMessages,
    openShareModal,
  } = useChatStore();

  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);

  const activeSession = getActiveSession();

  const activePersona: EnrichedChatPersona =
    SYSTEM_CHAT_PERSONAS.find((p) => p.id === activePersonaId) ||
    SYSTEM_CHAT_PERSONAS[0];

  const activeModel =
    CHAT_AI_MODELS.find((m) => m.code === activeModelCode) || CHAT_AI_MODELS[0];

  const PersonaIcon = getPersonaIcon(activePersona.iconName);

  const handleSelectPersona = (persona: EnrichedChatPersona) => {
    setActivePersona(persona.id);
    if (persona.recommendedModel) {
      setActiveModel(persona.recommendedModel);
    }
  };

  const handleStopGenerating = () => {
    ChatStreamService.abortCurrentStream();
    setIsGenerating(false);
  };

  const handleSendMessage = async (payload: MultimodalMessagePayload) => {
    let currentSession = activeSession;
    if (!currentSession) {
      const newId = createSession();
      currentSession = useChatStore.getState().sessions.find((s) => s.id === newId);
    }

    if (!currentSession) return;

    const sessionId = currentSession.id;

    // 1. Update session title if first message
    const isDefaultTitle =
      currentSession.totalMessages === 0 &&
      (currentSession.title.startsWith(t('chat.defaultConversationWith', { name: '' }).trim()) ||
        /^(Cu\u1ed9c tr\u00f2 chuy\u1ec7n v\u1edbi|Conversation with)/i.test(currentSession.title));

    if (isDefaultTitle) {
      const shortTitle =
        payload.content.slice(0, 36) + (payload.content.length > 36 ? '...' : '');
      updateSessionTitle(sessionId, shortTitle);
    }

    // 2. Append User Message
    addMessage(sessionId, {
      role: 'user',
      content: payload.content,
      attachments: payload.attachments,
    });
    incrementSessionMessages(sessionId);

    // 3. Create Assistant Placeholder Message
    const assistantMsgId = addMessage(sessionId, {
      role: 'assistant',
      content: '',
      isStreaming: true,
      modelCode: activeModel.code,
      personaId: activePersona.id,
    });
    incrementSessionMessages(sessionId);
    setIsGenerating(true);

    // 4. Stream Response via SSE Client
    try {
      await ChatStreamService.streamChatMessage({
        workspaceId: 'default-workspace',
        sessionId,
        message: payload.content,
        model: activeModel.code,
        attachments: payload.attachments,
        onCitations: (citations) => {
          finalizeMessage(sessionId, assistantMsgId, { citations });
        },
        onDelta: (deltaText) => {
          appendMessageDelta(sessionId, assistantMsgId, deltaText);
        },
        onDone: (data) => {
          finalizeMessage(sessionId, assistantMsgId, {
            isStreaming: false,
            tokensUsed: data.tokensUsed,
            creditCost: data.costCredits,
          });
          setIsGenerating(false);
        },
        onError: (errorMessage) => {
          updateMessageContent(
            sessionId,
            assistantMsgId,
            `\n\n> ⚠️ *${errorMessage}*`
          );
          finalizeMessage(sessionId, assistantMsgId, { isStreaming: false });
          setIsGenerating(false);
        },
      });
    } catch {
      finalizeMessage(sessionId, assistantMsgId, { isStreaming: false });
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async (targetMsg: ChatMessageItem, targetModelCode?: string) => {
    if (!activeSession || isGenerating) return;

    const messages = getSessionMessages(activeSession.id);
    const targetIndex = messages.findIndex((m) => m.id === targetMsg.id);
    if (targetIndex === -1) return;

    // Find the preceding user query
    let userPrompt = '';
    for (let i = targetIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        userPrompt = messages[i].content;
        break;
      }
    }

    if (!userPrompt) return;

    const chosenModel = targetModelCode || targetMsg.modelCode || activeModel.code;

    // Reset target assistant message content
    updateMessageContent(activeSession.id, targetMsg.id, '');
    finalizeMessage(activeSession.id, targetMsg.id, { isStreaming: true });
    setIsGenerating(true);

    try {
      await ChatStreamService.streamChatMessage({
        workspaceId: 'default-workspace',
        sessionId: activeSession.id,
        message: userPrompt,
        model: chosenModel,
        onCitations: (citations) => {
          finalizeMessage(activeSession.id, targetMsg.id, { citations });
        },
        onDelta: (deltaText) => {
          appendMessageDelta(activeSession.id, targetMsg.id, deltaText);
        },
        onDone: (data) => {
          finalizeMessage(activeSession.id, targetMsg.id, {
            isStreaming: false,
            tokensUsed: data.tokensUsed,
            creditCost: data.costCredits,
          });
          setIsGenerating(false);
        },
        onError: (err) => {
          updateMessageContent(
            activeSession.id,
            targetMsg.id,
            `\n\n> ⚠️ *${err}*`
          );
          finalizeMessage(activeSession.id, targetMsg.id, { isStreaming: false });
          setIsGenerating(false);
        },
      });
    } catch {
      finalizeMessage(activeSession.id, targetMsg.id, { isStreaming: false });
      setIsGenerating(false);
    }
  };

  return (
    <div className="relative flex h-[calc(100vh-14rem)] min-h-[640px] w-full overflow-hidden rounded-2xl border border-surface-border bg-surface shadow-glass">
      {/* 1. Left Sidebar (Collapsible) */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isSidebarOpen ? 'w-72 sm:w-80' : 'w-0 overflow-hidden'
        }`}
      >
        <ChatSidebar />
      </div>

      {/* 2. Main Chat Conversation Panel */}
      <div className="flex flex-1 flex-col overflow-hidden bg-surface-elevated/20">
        {/* Chat Studio Top Header Bar */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-surface-border bg-surface/90 backdrop-blur-md z-10">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleSidebar}
              title={isSidebarOpen ? t('chat.collapseSidebar') : t('chat.expandSidebar')}
              className="p-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors"
            >
              {isSidebarOpen ? (
                <PanelLeftClose className="w-4 h-4" />
              ) : (
                <PanelLeft className="w-4 h-4" />
              )}
            </button>

            {/* Persona Switcher Trigger */}
            <button
              type="button"
              onClick={() => setIsPersonaModalOpen(true)}
              className="group flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl border border-surface-border bg-surface-elevated/70 hover:border-primary/40 hover:bg-surface-elevated transition-all text-left shadow-sm"
            >
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/30 flex items-center justify-center text-primary flex-shrink-0 group-hover:scale-105 transition-transform">
                <PersonaIcon className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <span className="font-heading font-bold text-xs text-foreground group-hover:text-primary transition-colors">
                    {activePersona.name}
                  </span>
                  <span className="text-[9px] px-1.5 py-0.2 rounded-full bg-primary/10 text-primary font-bold">
                    {activePersona.category}
                  </span>
                </div>
                <span className="text-[10px] text-foreground-muted line-clamp-1">
                  {t('chat.switchPersona')}
                </span>
              </div>
            </button>
          </div>

          {/* Right Header Utilities: Model Dropdown & Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Knowledge Base (RAG) Pill */}
            <button
              type="button"
              onClick={onNavigateToRAG}
              className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground-muted hover:text-foreground hover:border-accent/40 transition-colors"
              title={t('chat.ragKnowledgeTooltip')}
            >
              <Database className="w-3.5 h-3.5 text-accent" />
              <span className="font-medium text-[11px]">{t('chat.ragKnowledge')}</span>
            </button>

            {/* Model Selector Dropdown */}
            <ModelSelectorDropdown
              activeModelCode={activeModelCode}
              onSelectModel={setActiveModel}
            />

            {/* Pin Active Session */}
            {activeSession && (
              <button
                type="button"
                onClick={() => togglePinSession(activeSession.id)}
                title={activeSession.isPinned ? t('chat.unpinSession') : t('chat.pinSession')}
                className={`p-2 rounded-xl border border-surface-border text-xs transition-colors ${
                  activeSession.isPinned
                    ? 'bg-accent/15 text-accent border-accent/30'
                    : 'bg-surface-elevated text-foreground-muted hover:text-foreground'
                }`}
              >
                {activeSession.isPinned ? (
                  <PinOff className="w-3.5 h-3.5" />
                ) : (
                  <Pin className="w-3.5 h-3.5" />
                )}
              </button>
            )}

            {/* Share Active Session */}
            {activeSession && (
              <button
                type="button"
                onClick={() => openShareModal(activeSession.id)}
                title={t('chat.shareChat')}
                className="p-2 rounded-xl border border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground text-xs transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Quick New Chat Button */}
            <button
              type="button"
              onClick={() => createSession()}
              title={t('chat.newChatTooltip')}
              className="p-2 rounded-xl bg-primary text-primary-fg hover:opacity-95 shadow-sm transition-opacity"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </header>

        {/* Chat Messages Body Scroll Viewport */}
        {(!activeSession || activeSession.totalMessages === 0) ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 scrollbar-thin">
            {/* Welcome Screen when session is fresh */}
            <div className="max-w-2xl mx-auto py-8 space-y-8 animate-in fade-in duration-300">
              {/* Persona Hero Greeting */}
              <div className="text-center space-y-3">
                <div className="relative inline-block">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-primary/30 to-accent/30 border border-primary/40 flex items-center justify-center text-primary shadow-xl mx-auto">
                    <PersonaIcon className="w-10 h-10" />
                  </div>
                  <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 border-2 border-surface flex items-center justify-center text-[10px] text-white">
                    <Sparkles className="w-3.5 h-3.5" />
                  </span>
                </div>

                <div className="space-y-1">
                  <h2 className="text-2xl font-heading font-extrabold text-foreground tracking-tight">
                    {activePersona.name}
                  </h2>
                  <p className="text-xs text-primary font-semibold">
                    {activePersona.roleDescription}
                  </p>
                </div>

                <div className="p-4 rounded-2xl border border-surface-border bg-surface/80 text-xs text-foreground-muted leading-relaxed max-w-lg mx-auto shadow-sm">
                  &ldquo;{activePersona.firstMessage}&rdquo;
                </div>
              </div>

              {/* Starter Questions Grid (1-Click Prompts) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between px-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-foreground-muted">
                    {t('chat.quickStarters')}
                  </span>
                  <span className="text-[11px] text-primary font-medium">
                    {t('chat.oneClickAsk')}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activePersona.starterQuestions.map((question, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSendMessage({ content: question, attachments: [] })}
                      className="group p-3.5 rounded-xl border border-surface-border bg-surface/60 hover:bg-surface hover:border-primary/40 hover:shadow-md text-left transition-all duration-200 flex items-center justify-between gap-3"
                    >
                      <span className="text-xs text-foreground-muted group-hover:text-foreground line-clamp-2 leading-relaxed">
                        {question}
                      </span>
                      <ArrowRight className="w-4 h-4 text-foreground-muted/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Meta tags info */}
              <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] text-foreground-muted">
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface border border-surface-border">
                  <Bot className="w-3.5 h-3.5 text-primary" />
                  {t('chat.activeModelLabel', { model: activeModel.name, provider: activeModel.providerLabel })}
                </span>
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface border border-surface-border">
                  <Database className="w-3.5 h-3.5 text-accent" />
                  {t('chat.vectorizeReady')}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <ChatMessageFeed
            sessionId={activeSession.id}
            onStopGenerating={handleStopGenerating}
            onRegenerate={handleRegenerate}
            className="flex-1"
          />
        )}

        {/* 3. Bottom Multimodal Input Area */}
        <div className="p-3 sm:p-4 border-t border-surface-border bg-surface/90 backdrop-blur-md">
          <div className="max-w-3xl mx-auto">
            <MultimodalChatInput
              onSendMessage={handleSendMessage}
              onOpenTemplates={onNavigateToTemplates}
              placeholder={t('chat.askPersonaPlaceholder', { name: activePersona.name })}
              modelName={activeModel.name}
              creditCost={activeModel.creditCost}
            />
          </div>
        </div>
      </div>

      {/* 4. Persona Selection Library Modal */}
      <PersonaSelectorModal
        isOpen={isPersonaModalOpen}
        onClose={() => setIsPersonaModalOpen(false)}
        activePersonaId={activePersonaId}
        onSelectPersona={handleSelectPersona}
      />

      {/* 5. Citations Inspector Drawer */}
      <CitationsDrawer />

      {/* 6. Share Chat Session Modal */}
      <ShareChatModal />
    </div>
  );
};
